/**
 * Petmall — "Научи повече" as an info mark on the Регулярна доставка option.
 *
 * Recharge renders its own trigger inside the subscription option's sub-container, which
 * exists only while that option is selected: with "Еднократна покупка" preselected the
 * explanation was unreachable. The mark sits next to the option's label instead, so it is
 * there whichever option is selected, and Recharge's own trigger is hidden.
 *
 * The dialog is ours rather than Recharge's: the same learnMoreContent from the widget
 * config, rendered inside a shadow root so the <style> block that ships with it (it styles
 * a bare `img` selector) cannot reach the rest of the page.
 */
(function () {
  const MARK_CLASS = 'petmall-rc-info';
  const STYLE_ID = 'petmall-rc-info-style';
  const ICON_SRC =
    'https://cdn.shopify.com/s/files/1/1057/7975/7405/files/info-circle_1.svg?v=1792571896';

  /** The widget config nests the content a few levels deep and the shape varies, so look for it. */
  function learnMoreContent() {
    const seen = new Set();
    function walk(node, depth) {
      if (!node || depth > 8 || typeof node !== 'object' || seen.has(node)) return null;
      seen.add(node);
      if (typeof node.learnMoreContent === 'string' && node.learnMoreContent.trim()) {
        return node.learnMoreContent;
      }
      for (const key of Object.keys(node)) {
        const found = walk(node[key], depth + 1);
        if (found) return found;
      }
      return null;
    }
    try {
      return walk(window.SubscriptionWidgetConfig && window.SubscriptionWidgetConfig.configs, 0);
    } catch (e) {
      return null;
    }
  }

  function ensureStyle(root) {
    if (root.getElementById && root.getElementById(STYLE_ID)) return;
    if (root.querySelector('#' + STYLE_ID)) return;
    const style = document.createElement('style');
    style.id = STYLE_ID;
    style.textContent =
      '.' + MARK_CLASS + '{display:inline-flex;align-items:center;justify-content:center;' +
      'width:16px;height:16px;margin-inline-start:6px;padding:0;border:0;background:none;' +
      'cursor:pointer;vertical-align:middle;flex:0 0 auto;}' +
      '.' + MARK_CLASS + ' img{width:16px;height:16px;display:block;}' +
      /* Recharge's own trigger lives in the shadow root of <rc-learn-more>, so the host
         element is what can be hidden from here. */
      'rc-learn-more,.rc-learn-more{display:none!important;}';
    root.appendChild(style);
  }

  // ---- dialog -------------------------------------------------------------
  let dialog = null;

  /* The host carries display:flex inline, which outranks the browser's own
     [hidden] { display: none } — so closing has to set the display too. */
  function closeDialog() {
    if (dialog) {
      dialog.hidden = true;
      dialog.style.display = 'none';
    }
    document.documentElement.style.overflow = '';
  }

  function buildDialog(content) {
    const host = document.createElement('div');
    host.className = 'petmall-rc-modal';
    host.hidden = true;
/* Above the sticky header and the sticky buy box, which both outrank a low z-index. */
    host.style.cssText =
      'position:fixed;inset:0;z-index:9999;display:flex;align-items:center;justify-content:center;' +
      'padding:16px;background:rgba(0,0,0,0.5);';
    host.style.display = 'none';
    host.addEventListener('click', (event) => {
      if (event.target === host) closeDialog();
    });


    const panel = document.createElement('div');
    /* The panel's content lives in its shadow root, which leaves it `div:empty` for the
       theme's own rule that hides empty divs — hence the class and the explicit display. */
    panel.className = 'no-empty';
    panel.style.cssText =
      'position:relative;display:block;background:#fff;border-radius:16px;max-width:720px;' +
      'width:100%;max-height:85vh;overflow:auto;padding:24px;';

    /* The content carries its own stylesheet, so it lives in a shadow root of its own. */
    const shadow = panel.attachShadow({ mode: 'open' });
    const wrap = document.createElement('div');
    wrap.innerHTML = content;
    const base = document.createElement('style');
    base.textContent =
      ':host{display:block;font-family:Inter,sans-serif;color:#111111;}' +
      'img{max-width:100%;}' +
      '.learn-more-footer button{background:#111;color:#fff;border:0;border-radius:8px;' +
      'padding:12px 24px;font-size:16px;font-weight:600;cursor:pointer;font-family:inherit;}';
    /* Appended after the content so it also outranks the stylesheet that ships inside
       learnMoreContent: the headings there are sized for a full-width modal and read as
       oversized on a phone. */
    const compact = document.createElement('style');
    compact.textContent =
      '.learn-more-header{font-size:1.6em;line-height:1.2;}' +
      '.learn-more-group-item h2{font-size:1.4em;line-height:1.1;' +
      'margin-block-start:0.5em;margin-block-end:0.25em;}' +
      '.learn-more-group-item p{font-size:1.2em;line-height:1.1;}' +
      '@media screen and (max-width:768px){' +
      '.learn-more-header{font-size:1.5em;}' +
      '.learn-more-group-item h2{font-size:1.2em;}' +
      '.learn-more-group-item p{font-size:1em;}}';

    shadow.appendChild(base);
    shadow.appendChild(wrap);
    shadow.appendChild(compact);
    shadow.addEventListener('click', (event) => {
      if (event.target.closest && event.target.closest('[data-dismiss-modal]')) closeDialog();
    });

    host.appendChild(panel);
    document.body.appendChild(host);

    document.addEventListener('keydown', (event) => {
      if (event.key === 'Escape') closeDialog();
    });
    return host;
  }

  function openDialog() {
    const content = learnMoreContent();
    if (!content) return;
    if (!dialog) dialog = buildDialog(content);
    dialog.hidden = false;
    dialog.style.display = 'flex';
    document.documentElement.style.overflow = 'hidden';
  }

  // ---- mark ---------------------------------------------------------------
  function addMark(root) {
    const option = root.querySelector('.rc-purchase-option__subscription');
    if (!option) return;
    const selector = option.querySelector('.rc-purchase-option__selector') || option;
    if (selector.querySelector('.' + MARK_CLASS)) return;

    const button = document.createElement('button');
    button.type = 'button';
    button.className = MARK_CLASS;
    button.setAttribute('aria-label', 'Научи повече за регулярната доставка');
    button.innerHTML = '<img src="' + ICON_SRC + '" alt="" width="16" height="16" loading="lazy">';

    /* The mark sits inside the option's <label>, so a plain click would also tick the radio. */
    ['click', 'mousedown', 'pointerdown'].forEach((type) => {
      button.addEventListener(
        type,
        (event) => {
          event.preventDefault();
          event.stopPropagation();
          if (type === 'click') openDialog();
        },
        true
      );
    });

    selector.appendChild(button);
  }

  function apply(widget) {
    const root = widget.shadowRoot;
    if (!root) return;
    ensureStyle(root);
    addMark(root);
  }

  function start(widget) {
    let queued = false;
    const observer = new MutationObserver(() => {
      if (queued) return;
      queued = true;
      window.requestAnimationFrame(() => {
        queued = false;
        observer.disconnect();
        apply(widget);
        observer.observe(widget.shadowRoot, { childList: true, subtree: true });
      });
    });
    apply(widget);
    observer.observe(widget.shadowRoot, { childList: true, subtree: true });
  }

  function init() {
    let tries = 0;
    const timer = window.setInterval(() => {
      tries += 1;
      const widget = document.querySelector('recharge-subscription-widget');
      if (widget && widget.shadowRoot && widget.shadowRoot.querySelector('.rc-purchase-option')) {
        window.clearInterval(timer);
        start(widget);
      } else if (tries > 60) {
        window.clearInterval(timer);
      }
    }, 250);
  }

  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', init);
  else init();
})();
